import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { FormattedMessage, defineMessages } from 'react-intl';

import Meta from 'Components/Utility/SEO/Meta';
import { fetchCookieData, removeConsent, openCookieCollapsible } from 'Redux/ducks/cookie';
import store from 'Redux/store';

const defaultSettings = {
  necessary: true,
  preferences: true,
  statistics: true,
  marketing: true,
  unclassified: true,
};

const categories = Object.keys(defaultSettings);

const messages = defineMessages({
  title: {
    id: 'cookie.title',
    defaultMessage: 'Cookie Policy',
  },
});

function CookiePolicy({ open, consent, intl }) {
  const { formatMessage } = intl;

  const [cookieData, setCookieData] = useState({});

  useEffect(() => {
    fetchCookieData().then(setCookieData);
  }, []);

  const changeConsent = () => {
    store.dispatch(openCookieCollapsible());
  };

  const withdrawConsent = () => {
    store.dispatch(removeConsent());
  };

  const tabLabels = {
    necessary: 'Necessary',
    preferences: 'Preferences',
    statistics: 'Statistics',
    marketing: 'Marketing',
    unclassified: 'Unclassified',
  };

  const tabDescriptions = {
    necessary: 'Necessary cookies are those which are vital to the proper function of the website',
    preferences: 'Preference cookies enable a website to remember information that changes the way the website behaves or looks, like your preferred language or the region that you are in.',
    statistics: 'Statistics cookies allow a website to be improved for users based on statistics on how it is used',
    marketing: 'Cookies used for marketing purposes',
    unclassified: 'Cookies that do not fit into any of the other categories',
  };

  /* eslint-disable max-len */

  const acceptedCategories = categories
    .map(c => (consent[c] ? c.charAt(0).toUpperCase() + c.slice(1) : null))
    .filter(Boolean);

  const acceptedCount = acceptedCategories.length;

  let cookieConsentString = 'Not consented';

  if (acceptedCount > 0 && acceptedCount < 5) {
    cookieConsentString = `Some Cookies Allowed (${acceptedCategories.join(', ')})`;
  }
  if (acceptedCount === 5) {
    cookieConsentString = `Allow all cookies (${acceptedCategories.join(', ')})`;
  }

  return (
    <div>
      <Meta
        brandTitle
        title={formatMessage(messages.title)}
        url="cookie-policy"
      />
      <div>
        <div className="container" lang="en">
          <div className="row justify-content-md-center">
            <div className="col-md-12">
              <h1 className="pt-5 pb-3">
                <FormattedMessage id="cookie.title" defaultMessage="Cookie Policy" />
              </h1>
              <div>
                <p>This website uses cookies. We use cookies to personalise content and ads, to provide social media features and to analyse our traffic. We also share information about your use of our site with our social media, advertising and analytics partners who may combine it with other information that you’ve provided to them or that they’ve collected from your use of their services. You consent to our cookies if you continue to use our website.</p>
                <p>Cookies are small text files that can be used by websites to make a user's experience more efficient.</p>
                <p>The law states that we can store cookies on your device if they are strictly necessary forthe operation of this site. For all other types of cookies we need your permission.</p>
                <p>This site uses different types of cookies. Some cookies are placed by third party services that appear on our pages.</p>
                <p>You can at any time change or withdraw your consent from the Cookie Policy</p>
                <p>Learn more about who we are, how you can contact us and how we process personal data in our <a href={'https://www.fandom.com/privacy-policy'} target="_blank" rel="noopener noreferrer">Privacy Policy</a></p>
                <p>Your consent applies to the following domains: www.fanatical.com</p>
                <p>
                  <span>Your current state: </span>
                  <strong>{cookieConsentString}</strong>
                </p>
                {consent.stamp &&
                  <p>
                    <span>Your consent ID: </span>
                    <strong>{consent.stamp}</strong>
                  </p>
                }
              </div>
              <div className="d-none d-lg-block">
                <p>
                  {!open &&
                    <button type="button" className="btn btn-primary p-2 mr-2" onClick={() => changeConsent()}>
                      <span>
                        <FormattedMessage id="cookie.policy.consentbutton1" defaultMessage="Change your consent" />
                      </span>
                    </button>
                  }
                  {acceptedCount > 1 &&
                    <button type="button" className="btn btn-primary p-2" onClick={() => withdrawConsent()}>
                      <span>
                        <FormattedMessage id="cookie.policy.consentbutton2" defaultMessage="Withdraw your consent" />
                      </span>
                    </button>
                  }
                </p>
              </div>

              <hr />

              {consent.utc &&
                <p>
                  <span>Last Updated: </span>
                  <span>{new Date(consent.utc).toString()}</span>
                </p>
              }

              {cookieData.last_modified &&
                <p>Cookie declaration last updated on {new Date(cookieData.last_modified * 1000).toString()}</p>
              }

              {categories.map(category =>
                <div key={category}>
                  <h3>{tabLabels[category]}</h3>
                  <p>
                    {tabDescriptions[category]}
                  </p>
                  {cookieData[`cookieTable${category.charAt(0).toUpperCase() + category.slice(1)}`] &&
                    <table className="table">
                      <thead>
                        <tr>
                          <th scope="col">
                            Name
                          </th>
                          <th scope="col">
                            Provider
                          </th>
                          <th scope="col">
                            Purpose
                          </th>
                          <th scope="col">
                            Expiry
                          </th>
                          <th scope="col">
                            Type
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {cookieData[`cookieTable${category.charAt(0).toUpperCase() + category.slice(1)}`].map(cookie =>
                          <tr key={`${category}cookies${cookie.Name}`}>
                            <td className="table-column-name">{cookie.Name}</td>
                            <td className="table-column-provider">{cookie.Provider}</td>
                            <td className="table-column-purpose">{cookie.Purpose}</td>
                            <td className="table-column-expiry">{cookie.Expiry}</td>
                            <td className="table-column-type">{cookie.Type}</td>
                          </tr>,
                        )}
                      </tbody>
                    </table>
                  }
                </div>,
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

CookiePolicy.propTypes = {
  open: PropTypes.bool,
  consent: PropTypes.shape({}),
};

CookiePolicy.defaultProps = {
  open: false,
  consent: {},
};

const mapStateToProps = ({
  cookie: { open, consent },
}) => ({
  open,
  consent,
});

export default connect(mapStateToProps, {})(CookiePolicy);
